import React, { useEffect } from 'react';
import { Form, Radio, Checkbox, Input, Typography, Divider, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { addVisualIndicators } from '../utils/healthSurveyValidation';

const { Title, Text } = Typography;
const { TextArea } = Input;

const PublicGeneralHealthAssessmentForm = ({ form, initialData }) => {
  const { t } = useTranslation();

  // Add visual indicators when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      addVisualIndicators('public-general-health-form');
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div id="public-general-health-form" style={{ padding: '16px 0' }}>
      <Title level={4} style={{ marginBottom: 24 }}>
        {t('healthSurvey.generalHealthAssessment')}
      </Title>

      <Form
        form={form}
        layout="vertical"
        initialValues={initialData}
        size="large"
      >
        {/* Question 1: Life Changes */}
        <Form.Item
          name="lifeChanges"
          label={
            <Text strong>
              {t('healthSurvey.questions.lifeChanges')}
            </Text>
          }
        >
          <Checkbox.Group
            style={{ width: '100%' }}
            options={[
              { label: t('healthSurvey.options.lifeChanges.residence'), value: 'residence' },
              { label: t('healthSurvey.options.lifeChanges.marital_family'), value: 'marital_family' },
              { label: t('healthSurvey.options.lifeChanges.employment'), value: 'employment' },
              { label: t('healthSurvey.options.lifeChanges.no_changes'), value: 'no_changes' },
            ]}
          />
        </Form.Item>

        <Divider />

        {/* Question 2: New Diagnoses */}
        <Form.Item
          name="newDiagnosis"
          label={
            <Text strong>
              {t('healthSurvey.questions.newDiagnosis')} <span style={{ color: '#ff4d4f' }}>*</span>
            </Text>
          }
          rules={[
            {
              required: true,
              message: t('healthSurvey.validation.generalHealthRequired')
            }
          ]}
        >
          <Radio.Group>
            <Space direction="vertical">
              <Radio value="yes">{t('healthSurvey.options.yesNo.yes')}</Radio>
              <Radio value="no">{t('healthSurvey.options.yesNo.no')}</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.newDiagnosis !== currentValues.newDiagnosis}
        >
          {({ getFieldValue }) => (
            <Form.Item
              name="newDiagnosisDetails"
              label={<Text>{t('healthSurvey.questions.newDiagnosisDetails')}</Text>}
            >
              <TextArea
                rows={3}
                placeholder={t('healthSurvey.questions.newDiagnosisDetails')}
                disabled={getFieldValue('newDiagnosis') !== 'yes'}
              />
            </Form.Item>
          )}
        </Form.Item>

        <Divider />

        {/* Question 3: Hospitalizations */}
        <Form.Item
          name="hospitalization"
          label={
            <Text strong>
              {t('healthSurvey.questions.hospitalization')} <span style={{ color: '#ff4d4f' }}>*</span>
            </Text>
          }
          rules={[
            {
              required: true,
              message: t('healthSurvey.validation.generalHealthRequired')
            }
          ]}
        >
          <Radio.Group>
            <Space direction="vertical">
              <Radio value="yes">{t('healthSurvey.options.yesNo.yes')}</Radio>
              <Radio value="no">{t('healthSurvey.options.yesNo.no')}</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.hospitalization !== currentValues.hospitalization}
        >
          {({ getFieldValue }) => (
            <Form.Item
              name="hospitalizationDetails"
              label={<Text>{t('healthSurvey.questions.hospitalizationDetails')}</Text>}
            >
              <TextArea
                rows={3}
                placeholder={t('healthSurvey.questions.hospitalizationDetails')}
                disabled={getFieldValue('hospitalization') !== 'yes'}
              />
            </Form.Item>
          )}
        </Form.Item>

        <Divider />

        {/* Question 4: Current Medications */}
        <Form.Item
          name="currentMedications"
          label={
            <Text strong>
              {t('healthSurvey.questions.currentMedications')}
            </Text>
          }
        >
          <Checkbox.Group
            style={{ width: '100%' }}
            options={[
              { label: t('healthSurvey.options.currentMedications.compass_prescription'), value: 'compass_prescription' },
              { label: t('healthSurvey.options.currentMedications.external_prescription'), value: 'external_prescription' },
              { label: t('healthSurvey.options.currentMedications.otc_medication'), value: 'otc_medication' },
              { label: t('healthSurvey.options.currentMedications.supplements'), value: 'supplements' },
              { label: t('healthSurvey.options.currentMedications.no_medications'), value: 'no_medications' },
            ]}
          />
        </Form.Item>

        <Divider />

        {/* Question 5: Health Improvement */}
        <Form.Item
          name="healthImprovement"
          label={
            <Text strong>
              {t('healthSurvey.questions.healthImprovement')} <span style={{ color: '#ff4d4f' }}>*</span>
            </Text>
          }
          rules={[
            {
              required: true,
              message: t('healthSurvey.validation.generalHealthRequired')
            }
          ]}
        >
          <Radio.Group>
            <Space direction="vertical">
              <Radio value="very_significant">{t('healthSurvey.options.healthImprovement.very_significant')}</Radio>
              <Radio value="improved">{t('healthSurvey.options.healthImprovement.improved')}</Radio>
              <Radio value="not_clear">{t('healthSurvey.options.healthImprovement.not_clear')}</Radio>
              <Radio value="no_improvement">{t('healthSurvey.options.healthImprovement.no_improvement')}</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
      </Form>
    </div>
  );
};

export default PublicGeneralHealthAssessmentForm;
